import { useEffect, useState } from 'react'

interface ClickHintProps {
  /** The scene is drawing: the rings are raining into the tank. */
  ready: boolean
  /** The visitor has tossed the rings at least once: the hint has done its job. */
  tossed: boolean
  touch: boolean
  /** The visitor's best from an earlier visit: a returning player gets a number to beat. */
  best: number
}

/** The rings take about this long to land: the hint waits for them, so the eye follows the drop first. */
const AFTER_DROP_MS = 1000

/**
 * What to do and what for, centred under the links where the eye already is: a small pill that
 * comes in once the rings have landed and fades out with the first toss, when the score takes
 * over at the bottom. It hangs below the links without taking space, so the title never moves.
 */
export const ClickHint: React.FC<ClickHintProps> = ({ ready, tossed, touch, best }) => {
  const [shown, setShown] = useState(false)
  useEffect(() => {
    if (!ready) return
    const id = window.setTimeout(() => setShown(true), AFTER_DROP_MS)
    return () => window.clearTimeout(id)
  }, [ready])
  if (!shown) return null
  return (
    <div
      aria-hidden={tossed}
      className={`pointer-events-none absolute inset-x-0 top-full mt-6 flex justify-center transition-opacity duration-500 ease-out motion-reduce:transition-none md:mt-8 short:hidden ${
        tossed ? 'opacity-0' : 'animate-rise motion-reduce:animate-none'
      }`}
    >
      <p
        data-testid="click-hint"
        aria-live="polite"
        className="flex flex-col items-center gap-1 rounded-2xl border border-white/15 bg-black/65 px-5 py-2.5 text-center backdrop-blur-sm"
      >
        <span className="text-[11px] uppercase tracking-[0.2em] whitespace-nowrap text-white/90 md:text-xs md:tracking-[0.25em]">
          {touch ? 'Tap' : 'Click'} anywhere to toss the rings
        </span>
        <span className="text-[11px] tracking-[0.04em] whitespace-nowrap text-white/55 md:text-xs">
          {best > 0 ? `Your best: ${best} · beat it` : 'Hook them on the letters'}
        </span>
      </p>
    </div>
  )
}
